import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, CreditCard, Loader2, User, Mail, FileText, Truck } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useShop } from '../context/ShopContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../utils/apiError';
import { validateCustomerPassword } from '../utils/customerPassword';
import { buildLoginUrl, buildRegisterUrl } from '../utils/safeRedirect';

const API_URL = process.env.REACT_APP_API_URL;

export default function CheckoutPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isAuthenticated, login, register, getAuthHeaders } = useAuth();
    const { cart, clearCart, addToCart } = useCart();
    const { merchantId } = useShop();
    
    const [orderComplete, setOrderComplete] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState({ paypal_enabled: false, stripe_enabled: false, cod_enabled: true });
    const [selectedPayment, setSelectedPayment] = useState('');
    const [loadingStripe, setLoadingStripe] = useState(false);
    const [loadingCOD, setLoadingCOD] = useState(false);
    
    // Shipping settings
    const [shippingSettings, setShippingSettings] = useState({
        shipping_enabled: true,
        shipping_flat_rate: 3.50,
        shipping_free_above: 50.00,
        shipping_free_enabled: false,
        shipping_acs: 0,
        shipping_speedex: 0,
        shipping_elta: 0,
        shipping_geniki: 0,
        shipping_boxnow: 0,
        cod_enabled: true,
        cod_fee: 2.00,
        tax_enabled: true,
        tax_rate: 24,
        tax_included_in_price: true,
        delivery_courier_enabled: true,
        delivery_cod_enabled: true,
        delivery_boxnow_enabled: true
    });
    const [selectedDelivery, setSelectedDelivery] = useState('courier');
    const [selectedDoorCarrier, setSelectedDoorCarrier] = useState('flat');
    const [boxnowLockerId, setBoxnowLockerId] = useState('');
    const [boxnowLockerLabel, setBoxnowLockerLabel] = useState('');
    
    // Guest checkout state
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [guestEmail, setGuestEmail] = useState('');
    const [isGuest, setIsGuest] = useState(false);
    
    // Auth form state
    const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
    const [authForm, setAuthForm] = useState({ name: '', email: '', password: '', subscribeNewsletter: false });
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    
    // Invoice state (τιμολόγιο)
    const [wantsInvoice, setWantsInvoice] = useState(false);
    const [invoiceData, setInvoiceData] = useState({
        company: '',
        profession: '',
        afm: '',
        doy: '',
        address: ''
    });
    
    const [shipping, setShipping] = useState({
        name: '',
        phone: '',
        address: '',
        city: '',
        postal: '',
        country: 'Ελλάδα'
    });

    const stripeSessionHandled = useRef(null);

    const checkStripePayment = useCallback(async (sessionId) => {
        try {
            const response = await axios.get(`${API_URL}/checkout/stripe/status/${sessionId}`);
            if (response.data.payment_status === 'paid') {
                setOrderComplete(true);
                await clearCart();
                toast.success('Payment completed successfully!');
            }
        } catch (error) {
            console.error('Failed to check payment status:', error);
        }
    }, [clearCart]);

    // Check for Stripe success (only once per session_id so toast doesn't repeat)
    const sessionIdFromUrl = searchParams.get('session_id');
    useEffect(() => {
        if (sessionIdFromUrl && stripeSessionHandled.current !== sessionIdFromUrl) {
            stripeSessionHandled.current = sessionIdFromUrl;
            checkStripePayment(sessionIdFromUrl);
        }
    }, [sessionIdFromUrl, checkStripePayment]);

    // Recover abandoned cart from email link
    useEffect(() => {
        const recoverToken = searchParams.get('recover');
        if (recoverToken && cart.items.length === 0) {
            (async () => {
                try {
                    const response = await axios.get(`${API_URL}/storefront/recover-cart/${recoverToken}`);
                    const { cart_items } = response.data;
                    for (const item of cart_items) {
                        await addToCart({
                            id: item.product_id,
                            name: item.name,
                            price: item.price,
                            image_url: item.image_url
                        }, item.quantity || 1);
                    }
                    toast.success('Το καλάθι σας επαναφέρθηκε!');
                } catch {
                    toast.error('Δεν ήταν δυνατή η επαναφορά του καλαθιού');
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Fetch payment methods and shipping settings
    useEffect(() => {
        fetchPaymentMethods();
        fetchShippingSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchantId]);

    // Pre-fill shipping name if logged in
    useEffect(() => {
        if (user && !shipping.name) {
            setShipping(prev => ({ ...prev, name: user.name || '' }));
        }
    }, [user, shipping.name]);

    // Track cart for logged-in users when they reach checkout
    useEffect(() => {
        if (isAuthenticated && user?.email && merchantId && cart.items.length > 0) {
            const timer = setTimeout(() => {
                trackAbandonedCart(user.email);
            }, 3000);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user, merchantId, cart.items.length]);

    const fetchShippingSettings = async () => {
        if (!merchantId) return;
        try {
            const response = await axios.get(`${API_URL}/store/${merchantId}/shipping`);
            setShippingSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch shipping settings:', error);
        }
    };

    const fetchPaymentMethods = async () => {
        if (!merchantId) return;
        try {
            const response = await axios.get(`${API_URL}/store/${merchantId}/payment-methods`);
            const methods = {
                ...response.data,
                cod_enabled: response.data.cod_enabled !== false // Default to true if not set
            };
            setPaymentMethods(methods);
            
            // Default payment selection priority: COD > Stripe > PayPal
            if (methods.cod_enabled) {
                setSelectedPayment('cod');
            } else if (methods.stripe_enabled) {
                setSelectedPayment('stripe');
            } else if (methods.paypal_enabled) {
                setSelectedPayment('paypal');
            }
        } catch (error) {
            console.error('Failed to fetch payment methods:', error);
            setPaymentMethods({ paypal_enabled: true, stripe_enabled: false, cod_enabled: true });
            setSelectedPayment('cod');
        }
    };

    const deliveryChoices = useMemo(() => {
        if (!shippingSettings.shipping_enabled) return [];
        const list = [];
        if (shippingSettings.delivery_courier_enabled !== false) {
            list.push({
                id: 'courier',
                name: 'Ταχυμεταφορά (πόρτα–πόρτα)'
            });
        }
        if (shippingSettings.delivery_cod_enabled !== false && shippingSettings.cod_enabled !== false) {
            list.push({
                id: 'cod',
                name: 'Αντικαταβολή (πόρτα–πόρτα)'
            });
        }
        if (
            shippingSettings.delivery_boxnow_enabled ||
            Number(shippingSettings.shipping_boxnow) > 0
        ) {
            list.push({
                id: 'boxnow',
                name: 'Box Now (locker)',
                cost: Number(shippingSettings.shipping_boxnow) || 0
            });
        }
        return list;
    }, [shippingSettings]);

    const doorCarrierChoices = useMemo(() => {
        const list = [
            {
                id: 'flat',
                name: 'Γενική αποστολή',
                cost: Number(shippingSettings.shipping_flat_rate) || 0
            }
        ];
        if (Number(shippingSettings.shipping_acs) > 0) {
            list.push({ id: 'acs', name: 'ACS', cost: Number(shippingSettings.shipping_acs) });
        }
        if (Number(shippingSettings.shipping_speedex) > 0) {
            list.push({ id: 'speedex', name: 'Speedex', cost: Number(shippingSettings.shipping_speedex) });
        }
        if (Number(shippingSettings.shipping_elta) > 0) {
            list.push({ id: 'elta', name: 'ΕΛΤΑ Courier', cost: Number(shippingSettings.shipping_elta) });
        }
        if (Number(shippingSettings.shipping_geniki) > 0) {
            list.push({ id: 'geniki', name: 'Γενική Ταχυδρομική', cost: Number(shippingSettings.shipping_geniki) });
        }
        return list;
    }, [shippingSettings]);

    useEffect(() => {
        if (!doorCarrierChoices.some((c) => c.id === selectedDoorCarrier)) {
            setSelectedDoorCarrier(doorCarrierChoices[0]?.id || 'flat');
        }
    }, [doorCarrierChoices, selectedDoorCarrier]);

    useEffect(() => {
        if (!deliveryChoices.length) return;
        if (!deliveryChoices.some((c) => c.id === selectedDelivery)) {
            setSelectedDelivery(deliveryChoices[0].id);
        }
    }, [deliveryChoices, selectedDelivery]);

    useEffect(() => {
        if (selectedDelivery === 'cod') {
            setSelectedPayment('cod');
            return;
        }
        if (selectedDelivery === 'courier') {
            if (paymentMethods.stripe_enabled) setSelectedPayment('stripe');
            else if (paymentMethods.paypal_enabled) setSelectedPayment('paypal');
            return;
        }
        if (selectedDelivery === 'boxnow') {
            if (paymentMethods.stripe_enabled) setSelectedPayment('stripe');
            else if (paymentMethods.paypal_enabled) setSelectedPayment('paypal');
            else if (paymentMethods.cod_enabled) setSelectedPayment('cod');
        }
    }, [selectedDelivery, paymentMethods.stripe_enabled, paymentMethods.paypal_enabled, paymentMethods.cod_enabled]);

    const baseContactOk = Boolean(shipping.name && shipping.phone && shipping.country);
    const addressOk = Boolean(shipping.address && shipping.city && shipping.postal);
    const boxnowOk =
        selectedDelivery !== 'boxnow' ||
        Boolean(String(boxnowLockerId).trim() || String(boxnowLockerLabel).trim());
    const isShippingComplete =
        baseContactOk && boxnowOk && (selectedDelivery === 'boxnow' ? true : addressOk);

    const getShippingCost = () => {
        if (!shippingSettings.shipping_enabled) return 0;
        if (shippingSettings.shipping_free_enabled && cart.total >= shippingSettings.shipping_free_above) {
            return 0;
        }
        if (selectedDelivery === 'boxnow') return Number(shippingSettings.shipping_boxnow) || 0;
        if (selectedDelivery === 'courier' || selectedDelivery === 'cod') {
            const row = doorCarrierChoices.find((c) => c.id === selectedDoorCarrier);
            return row ? row.cost : Number(shippingSettings.shipping_flat_rate) || 0;
        }
        return Number(shippingSettings.shipping_flat_rate) || 0;
    };

    const codFeeRate = Number(shippingSettings.cod_fee) || 0;
    const codFee = selectedPayment === 'cod' ? codFeeRate : 0;
    
    // Calculate shipping
    const shippingCost = getShippingCost();
    const selectedDoorCarrierLabel = doorCarrierChoices.find((c) => c.id === selectedDoorCarrier)?.name;

    // Check if free shipping applies
    const isFreeShipping = shippingSettings.shipping_free_enabled && cart.total >= shippingSettings.shipping_free_above;
    
    // Calculate tax (if not included in price)
    const calculateTax = () => {
        if (!shippingSettings.tax_enabled || shippingSettings.tax_included_in_price) return 0;
        return cart.total * (shippingSettings.tax_rate / 100);
    };
    
    const taxAmount = calculateTax();
    
    // Final total
    const totalWithFees = cart.total + shippingCost + codFee + taxAmount;

    const handleInputChange = (e) => {
        setShipping(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    // Handle proceeding - show login prompt for guests
    const handleProceedToPayment = () => {
        if (!isShippingComplete) {
            toast.error('Please fill in all shipping details');
            return;
        }
        
        if (!isAuthenticated && !isGuest) {
            setShowLoginPrompt(true);
        }
    };

    // Track abandoned cart (call when email is available)
    const trackAbandonedCart = useCallback(async (email) => {
        if (!email || !merchantId || cart.items.length === 0) return;
        try {
            await axios.post(`${API_URL}/storefront/track-cart`, {
                email,
                merchant_id: merchantId,
                cart_items: cart.items.map(i => ({
                    product_id: i.product_id,
                    name: i.name,
                    price: i.price,
                    quantity: i.quantity,
                    image_url: i.image_url,
                    subtotal: i.subtotal
                })),
                cart_total: cart.total,
                shipping_name: shipping.name || null,
                shipping_phone: shipping.phone || null
            });
        } catch (err) {
            // Silent fail - tracking is non-critical
        }
    }, [merchantId, cart.items, cart.total, shipping.name, shipping.phone]);

    // Continue as guest (email optional – without email they won't get order updates)
    const handleContinueAsGuest = () => {
        setIsGuest(true);
        setShowLoginPrompt(false);
        if (guestEmail && guestEmail.includes('@')) {
            trackAbandonedCart(guestEmail);
        }
    };

    // Handle login/register
    const handleAuth = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError('');
        
        try {
            if (authMode === 'register') {
                const pwErr = validateCustomerPassword(authForm.password);
                if (pwErr) {
                    setAuthError(pwErr);
                    return;
                }
            }

            let result;
            if (authMode === 'login') {
                result = await login(authForm.email, authForm.password);
            } else {
                result = await register(authForm.name, authForm.email, authForm.password, authForm.subscribeNewsletter);
            }
            
            if (result.success) {
                setShowLoginPrompt(false);
                toast.success(authMode === 'login' ? 'Logged in successfully!' : 'Account created!');
            } else {
                setAuthError(result.error);
            }
        } catch (error) {
            setAuthError('An error occurred. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const createOrder = async () => {
        try {
            const orderData = {
                shipping_name: shipping.name,
                shipping_phone: shipping.phone,
                shipping_address: shipping.address,
                shipping_city: shipping.city,
                shipping_postal: shipping.postal,
                shipping_country: shipping.country,
                payment_method: selectedPayment,
                merchant_id: merchantId,
                cart_items: cart.items,
                total_price: totalWithFees,
                // Invoice data
                wants_invoice: wantsInvoice,
                invoice_company: wantsInvoice ? invoiceData.company : null,
                invoice_profession: wantsInvoice ? invoiceData.profession : null,
                invoice_afm: wantsInvoice ? invoiceData.afm : null,
                invoice_doy: wantsInvoice ? invoiceData.doy : null,
                invoice_address: wantsInvoice ? invoiceData.address : null,
                // Shipping info
                shipping_method: selectedDelivery,
                delivery_option: selectedDelivery,
                shipping_carrier:
                    selectedDelivery === 'courier' || selectedDelivery === 'cod' ? selectedDoorCarrier : undefined,
                boxnow_locker_id: selectedDelivery === 'boxnow' ? boxnowLockerId.trim() || null : null,
                boxnow_locker_label: selectedDelivery === 'boxnow' ? boxnowLockerLabel.trim() || null : null,
                shipping_cost: shippingCost,
                cod_fee: codFee,
                // Tax info
                tax_amount: taxAmount,
                tax_rate: shippingSettings.tax_rate,
                tax_included_in_price: shippingSettings.tax_included_in_price
            };
            
            if (isGuest) {
                orderData.guest_email = guestEmail && guestEmail.trim() ? guestEmail.trim() : null;
            }
            
            const headers = isAuthenticated ? getAuthHeaders() : {};
            
            const response = await axios.post(`${API_URL}/storefront/orders`, orderData, { headers });
            return response.data;
        } catch (error) {
            toast.error(
                formatApiErrorDetail(error.response?.data?.detail, 'Σφάλμα δημιουργίας παραγγελίας')
            );
            throw error;
        }
    };
    
    // Handle COD order
    const handleCODOrder = async () => {
        try {
            setLoadingCOD(true);
            const order = await createOrder();
            setOrderId(order.id);
            setOrderComplete(true);
            await clearCart();
            toast.success('Η παραγγελία σας καταχωρήθηκε! Θα πληρώσετε στην παραλαβή.');
        } catch (error) {
            // Error handled in createOrder
        } finally {
            setLoadingCOD(false);
        }
    };

    const handleStripeCheckout = async () => {
        try {
            setLoadingStripe(true);
            const order = await createOrder();
            
            const response = await axios.post(`${API_URL}/checkout/stripe/create-session`, {
                order_id: order.id,
                origin_url: window.location.origin
            });
            
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Stripe checkout error:', error);
            toast.error(
                formatApiErrorDetail(error.response?.data?.detail, 'Failed to connect to Stripe')
            );
        } finally {
            setLoadingStripe(false);
        }
    };

    const handlePayPalSuccess = async (paypalOrderId) => {
        try {
            const order = await createOrder();
            setOrderId(order.id);
            setOrderComplete(true);
            await clearCart();
            toast.success('Order completed successfully!');
        } catch (error) {
            // Error handled in createOrder
        }
    };

    // Redirect to cart if empty (and not completing order)
    if (cart.items.length === 0 && !orderComplete && !searchParams.get('session_id')) {
        navigate('/cart');
        return null;
    }

    // Order complete screen
    if (orderComplete) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center max-w-md px-4"
                >
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                        <Check className="w-10 h-10 text-green-600" />
                    </div>
                    <h1 className="font-display text-3xl font-semibold text-primary mb-4">
                        Order Confirmed!
                    </h1>
                    <p className="text-muted-foreground mb-2">
                        Thank you for your purchase. {orderId && `Order #${orderId.slice(0, 8)}`}
                    </p>
                    <p className="text-sm text-muted-foreground mb-8">
                        You will receive a confirmation email shortly.
                    </p>
                    <Link to="/">
                        <Button className="rounded-full" data-testid="continue-shopping-btn">
                            Continue Shopping
                        </Button>
                    </Link>
                </motion.div>
            </div>
        );
    }

    const paypalClientId = paymentMethods.paypal_client_id || process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb';

    return (
        <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "EUR" }}>
            <div className="min-h-screen min-h-[100dvh] bg-background overflow-x-hidden">
                <Header />

                {/* Content */}
                <main className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12 w-full min-w-0">
                    <Link 
                        to="/cart" 
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Cart
                    </Link>

                    <h1 className="font-display text-4xl font-semibold text-primary mb-8">
                        Checkout
                    </h1>

                    <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 w-full min-w-0">
                        {/* Shipping Info */}
                        <div className="space-y-6 w-full min-w-0">
                            <h2 className="font-display text-xl font-semibold">Στοιχεία Αποστολής</h2>
                            
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Ονοματεπώνυμο</Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        placeholder="Ονοματεπώνυμο"
                                        value={shipping.name}
                                        onChange={handleInputChange}
                                        className="h-11"
                                        data-testid="shipping-name-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Τηλέφωνο</Label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        placeholder="6912345678"
                                        value={shipping.phone}
                                        onChange={handleInputChange}
                                        className="h-11"
                                        data-testid="shipping-phone-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="address">Διεύθυνση</Label>
                                    <Input
                                        id="address"
                                        name="address"
                                        placeholder="Οδός & αριθμός"
                                        value={shipping.address}
                                        onChange={handleInputChange}
                                        className="h-11"
                                        data-testid="shipping-address-input"
                                    />
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="city">Πόλη</Label>
                                        <Input
                                            id="city"
                                            name="city"
                                            placeholder="Πόλη"
                                            value={shipping.city}
                                            onChange={handleInputChange}
                                            className="h-11"
                                            data-testid="shipping-city-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="postal">Τ.Κ.</Label>
                                        <Input
                                            id="postal"
                                            name="postal"
                                            placeholder="Τ.Κ."
                                            value={shipping.postal}
                                            onChange={handleInputChange}
                                            className="h-11"
                                            data-testid="shipping-postal-input"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="country">Χώρα</Label>
                                    <Input
                                        id="country"
                                        name="country"
                                        placeholder="Χώρα"
                                        value={shipping.country}
                                        onChange={handleInputChange}
                                        className="h-11"
                                        data-testid="shipping-country-input"
                                    />
                                </div>
                            </div>
                            
                            {/* Invoice Section (Τιμολόγιο) */}
                            <div className="pt-4 border-t">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={wantsInvoice}
                                        onChange={(e) => setWantsInvoice(e.target.checked)}
                                        className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <span className="flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        Θέλω τιμολόγιο
                                    </span>
                                </label>
                                
                                {wantsInvoice && (
                                    <div className="mt-4 p-4 bg-gray-50 rounded-xl space-y-3">
                                        <div className="space-y-2">
                                            <Label>Επωνυμία Εταιρείας</Label>
                                            <Input
                                                placeholder="Εταιρεία Α.Ε."
                                                value={invoiceData.company}
                                                onChange={(e) => setInvoiceData({...invoiceData, company: e.target.value})}
                                                className="h-10"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Επάγγελμα</Label>
                                            <Input
                                                placeholder="Έμπορος"
                                                value={invoiceData.profession}
                                                onChange={(e) => setInvoiceData({...invoiceData, profession: e.target.value})}
                                                className="h-10"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div className="space-y-2">
                                                <Label>ΑΦΜ</Label>
                                                <Input
                                                    placeholder="123456789"
                                                    value={invoiceData.afm}
                                                    onChange={(e) => setInvoiceData({...invoiceData, afm: e.target.value})}
                                                    className="h-10"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>ΔΟΥ</Label>
                                                <Input
                                                    placeholder="Α' Αθηνών"
                                                    value={invoiceData.doy}
                                                    onChange={(e) => setInvoiceData({...invoiceData, doy: e.target.value})}
                                                    className="h-10"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Διεύθυνση Έδρας</Label>
                                            <Input
                                                placeholder="Οδός, Αριθμός, Πόλη"
                                                value={invoiceData.address}
                                                onChange={(e) => setInvoiceData({...invoiceData, address: e.target.value})}
                                                className="h-10"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payment Section */}
                            <div className="pt-6 border-t">
                                <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
                                    <CreditCard className="w-5 h-5" />
                                    Τρόπος Πληρωμής
                                </h2>
                                
                                {!isShippingComplete ? (
                                    <div className="bg-muted/50 rounded-xl p-4 text-center text-muted-foreground">
                                        Συμπληρώστε τα στοιχεία αποστολής για να συνεχίσετε
                                    </div>
                                ) : !isAuthenticated && !isGuest ? (
                                    <Button 
                                        onClick={handleProceedToPayment}
                                        className="w-full rounded-full h-12"
                                        data-testid="proceed-payment-btn"
                                    >
                                        <User className="w-4 h-4 mr-2" />
                                        Συνέχεια στην Πληρωμή
                                    </Button>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Payment Method Selection */}
                                        <div className="flex flex-col gap-3">
                                            {/* Cash on Delivery - Αντικαταβολή (όχι για αμιγώς προπληρωμένη ταχυμεταφορά) */}
                                            {selectedDelivery !== 'courier' && paymentMethods.cod_enabled && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedPayment('cod')}
                                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                                                        selectedPayment === 'cod' 
                                                            ? 'border-primary bg-primary/5' 
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                    data-testid="select-cod"
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                                        <span className="text-green-600 text-lg">💵</span>
                                                    </div>
                                                    <div className="text-left flex-1">
                                                        <p className="font-medium">Αντικαταβολή</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            Πληρωμή στην παραλαβή — χρέωση αντικαταβολίας +€
                                                            {shippingSettings.cod_fee.toFixed(2)}
                                                        </p>
                                                    </div>
                                                    {selectedPayment === 'cod' && (
                                                        <Check className="w-5 h-5 text-primary" />
                                                    )}
                                                </button>
                                            )}
                                            
                                            {/* Stripe Card */}
                                            {(selectedDelivery === 'courier' || selectedDelivery === 'boxnow') && paymentMethods.stripe_enabled && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedPayment('stripe')}
                                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                                                        selectedPayment === 'stripe' 
                                                            ? 'border-primary bg-primary/5' 
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                    data-testid="select-stripe"
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                                        <CreditCard className="w-5 h-5 text-purple-600" />
                                                    </div>
                                                    <div className="text-left flex-1">
                                                        <p className="font-medium">Κάρτα</p>
                                                        <p className="text-sm text-muted-foreground">Visa, Mastercard, κ.α.</p>
                                                    </div>
                                                    {selectedPayment === 'stripe' && (
                                                        <Check className="w-5 h-5 text-primary" />
                                                    )}
                                                </button>
                                            )}
                                            
                                            {/* PayPal */}
                                            {(selectedDelivery === 'courier' || selectedDelivery === 'boxnow') && paymentMethods.paypal_enabled && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedPayment('paypal')}
                                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                                                        selectedPayment === 'paypal' 
                                                            ? 'border-primary bg-primary/5' 
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                    data-testid="select-paypal"
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <span className="text-blue-600 font-bold text-sm">PP</span>
                                                    </div>
                                                    <div className="text-left flex-1">
                                                        <p className="font-medium">PayPal</p>
                                                        <p className="text-sm text-muted-foreground">Πληρωμή μέσω PayPal</p>
                                                    </div>
                                                    {selectedPayment === 'paypal' && (
                                                        <Check className="w-5 h-5 text-primary" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        {selectedDelivery === 'courier' && !paymentMethods.stripe_enabled && !paymentMethods.paypal_enabled && (
                                            <p className="text-sm text-muted-foreground">
                                                Για ταχυμεταφορά χρειάζεται online πληρωμή — ενεργοποιήστε Stripe ή PayPal στις ρυθμίσεις του καταστήματος.
                                            </p>
                                        )}

                                        {/* Payment Actions */}
                                        <div className="pt-4">
                                            {/* COD Button */}
                                            {selectedPayment === 'cod' && (
                                                <Button
                                                    onClick={handleCODOrder}
                                                    disabled={loadingCOD}
                                                    className="w-full rounded-full h-12 bg-green-600 hover:bg-green-700"
                                                    data-testid="cod-checkout-btn"
                                                >
                                                    {loadingCOD ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Υποβολή παραγγελίας...
                                                        </>
                                                    ) : (
                                                        <>
                                                            💵 Παραγγελία με Αντικαταβολή - €{totalWithFees.toFixed(2)}
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                            {selectedPayment === 'stripe' && (
                                                <Button
                                                    onClick={handleStripeCheckout}
                                                    disabled={loadingStripe}
                                                    className="w-full rounded-full h-12"
                                                    data-testid="stripe-checkout-btn"
                                                >
                                                    {loadingStripe ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                            Redirecting to Stripe...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CreditCard className="w-4 h-4 mr-2" />
                                                            Pay with Card - €{totalWithFees.toFixed(2)}
                                                        </>
                                                    )}
                                                </Button>
                                            )}

                                            {selectedPayment === 'paypal' && (
                                                <div data-testid="paypal-buttons" className="w-full max-w-full overflow-hidden">
                                                    <PayPalButtons
                                                        style={{ layout: "vertical", shape: "pill" }}
                                                        createOrder={(data, actions) => {
                                                            return actions.order.create({
                                                                purchase_units: [{
                                                                    amount: {
                                                                        value: totalWithFees.toFixed(2),
                                                                        currency_code: "EUR"
                                                                    }
                                                                }]
                                                            });
                                                        }}
                                                        onApprove={async (data, actions) => {
                                                            const details = await actions.order.capture();
                                                            await handlePayPalSuccess(details.id);
                                                        }}
                                                        onError={(err) => {
                                                            console.error('PayPal error:', err);
                                                            toast.error('Payment failed. Please try again.');
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-card lg:sticky lg:top-24 w-full min-w-0 max-w-sm mx-auto lg:max-w-none">
                                <h2 className="font-display text-lg sm:text-xl font-semibold mb-4 sm:mb-6">
                                    Σύνοψη Παραγγελίας
                                </h2>
                                
                                <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                                    {cart.items.map((item) => (
                                        <div key={item.product_id} className="flex gap-3">
                                            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                                                <img 
                                                    src={item.image_url?.startsWith('/api') ? `${API_URL.replace('/api', '')}${item.image_url}` : item.image_url}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-medium text-sm truncate">{item.name}</h4>
                                                <p className="text-xs text-muted-foreground">Ποσ: {item.quantity}</p>
                                                <p className="font-semibold text-sm tabular-nums">€{item.subtotal.toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Τρόπος παράδοσης (ταχυμεταφορά / αντικαταβολή / Box Now) */}
                                {shippingSettings.shipping_enabled && deliveryChoices.length > 0 && (
                                        <div className="mb-4 p-3 border rounded-xl bg-gray-50">
                                        <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                                            <Truck className="w-4 h-4" />
                                            Τρόπος παράδοσης
                                        </Label>
                                        <div className="space-y-2">
                                            {deliveryChoices.map((d) => (
                                                <button
                                                    key={d.id}
                                                    type="button"
                                                    onClick={() => setSelectedDelivery(d.id)}
                                                    className={`w-full flex items-center justify-between gap-2 rounded-lg border text-xs sm:text-sm px-3 py-2 sm:py-2 transition-all ${
                                                        selectedDelivery === d.id
                                                            ? 'border-primary bg-primary/5'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <span className="min-w-0 text-left truncate pr-2">
                                                        {d.name}
                                                    </span>
                                                    <span className="font-medium text-right tabular-nums shrink-0 whitespace-nowrap">
                                                        {d.id === 'boxnow' &&
                                                            `€${(Number(shippingSettings.shipping_boxnow) || 0).toFixed(2)}`}
                                                        {d.id === 'courier' &&
                                                            (selectedDelivery !== 'courier'
                                                                ? '—'
                                                                : isFreeShipping
                                                                  ? 'Δωρεάν'
                                                                  : `€${shippingCost.toFixed(2)}`)}
                                                        {d.id === 'cod' &&
                                                            (selectedDelivery !== 'cod'
                                                                ? codFeeRate > 0
                                                                    ? `+ €${codFeeRate.toFixed(2)} χρέωση`
                                                                    : '—'
                                                                : (() => {
                                                                      const ship = isFreeShipping ? 0 : shippingCost;
                                                                      const tot = ship + codFeeRate;
                                                                      if (tot === 0) return 'Δωρεάν';
                                                                      return `€${tot.toFixed(2)}`;
                                                                  })())}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                        {(selectedDelivery === 'courier' || selectedDelivery === 'cod') &&
                                            doorCarrierChoices.length > 1 &&
                                            !isFreeShipping && (
                                                <div className="mt-3 space-y-2 pt-2 border-t border-gray-200">
                                                    <p className="text-xs font-medium text-muted-foreground">
                                                        Εταιρεία ταχυμεταφοράς
                                                    </p>
                                                    <div className="space-y-1.5">
                                                        {doorCarrierChoices.map((c) => (
                                                            <button
                                                                key={c.id}
                                                                type="button"
                                                                onClick={() => setSelectedDoorCarrier(c.id)}
                                                                className={`w-full flex items-center justify-between p-2 rounded-lg border text-xs transition-all ${
                                                                    selectedDoorCarrier === c.id
                                                                        ? 'border-primary bg-primary/5'
                                                                        : 'border-gray-200 hover:border-gray-300'
                                                                }`}
                                                            >
                                                                <span>{c.name}</span>
                                                                <span className="font-medium">€{c.cost.toFixed(2)}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        {selectedDelivery === 'boxnow' && (
                                            <div className="mt-3 space-y-2 pt-2 border-t border-gray-200">
                                                <p className="text-xs text-muted-foreground">
                                                    Συμπληρώστε το σημείο παραλαβής (από την εφαρμογή/χάρτη Box Now).
                                                </p>
                                                <Input
                                                    placeholder="Κωδικός θυρίδας / APM (προαιρετικά)"
                                                    value={boxnowLockerId}
                                                    onChange={(e) => setBoxnowLockerId(e.target.value)}
                                                    className="h-9 text-sm"
                                                />
                                                <Input
                                                    placeholder="Περιγραφή σημείου (π.χ. περιοχή, διεύθυνση locker)"
                                                    value={boxnowLockerLabel}
                                                    onChange={(e) => setBoxnowLockerLabel(e.target.value)}
                                                    className="h-9 text-sm"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Free shipping banner */}
                                {shippingSettings.shipping_free_enabled && !isFreeShipping && (
                                    <div className="mb-4 p-3 bg-green-50 rounded-xl border border-green-200">
                                        <p className="text-sm text-green-800">
                                            🚚 Δωρεάν αποστολή για παραγγελίες άνω των €{shippingSettings.shipping_free_above.toFixed(2)}!
                                            <br />
                                            <span className="text-green-600 font-medium">
                                                Λείπουν €{(shippingSettings.shipping_free_above - cart.total).toFixed(2)}
                                            </span>
                                        </p>
                                    </div>
                                )}

                                {shippingSettings.shipping_enabled && deliveryChoices.length === 0 && (
                                    <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
                                        Δεν υπάρχει ενεργός τρόπος παράδοσης. Ο έμπορος πρέπει να ενεργοποιήσει τουλάχιστον μία επιλογή στις ρυθμίσεις αποστολής.
                                    </div>
                                )}

                                <div className="border-t pt-4 space-y-2">
                                    <div className="flex justify-between text-sm gap-3">
                                        <span className="text-muted-foreground min-w-0">Υποσύνολο</span>
                                        <span className="shrink-0 tabular-nums">€{cart.total.toFixed(2)}</span>
                                    </div>

                                    {shippingSettings.shipping_enabled && deliveryChoices.length > 0 && (
                                        <div className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1.5 sm:gap-3">
                                            <span className="text-muted-foreground min-w-0">Τρόπος παράδοσης</span>
                                            <div className="text-right min-w-0">
                                                <span
                                                    className={
                                                        selectedDelivery === 'boxnow'
                                                            ? 'font-medium text-violet-800'
                                                            : 'font-medium'
                                                    }
                                                >
                                                    {deliveryChoices.find((d) => d.id === selectedDelivery)?.name ?? '—'}
                                                </span>
                                                {selectedDelivery === 'boxnow' &&
                                                    (String(boxnowLockerId).trim() || String(boxnowLockerLabel).trim()) && (
                                                        <p className="text-xs text-muted-foreground mt-0.5 max-w-[220px] ml-auto">
                                                            {[
                                                                String(boxnowLockerId).trim(),
                                                                String(boxnowLockerLabel).trim()
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' · ')}
                                                        </p>
                                                    )}
                                                {(selectedDelivery === 'courier' || selectedDelivery === 'cod') &&
                                                    selectedDoorCarrierLabel && (
                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                            {selectedDoorCarrierLabel}
                                                        </p>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Shipping */}
                                    {shippingSettings.shipping_enabled && (
                                        <div className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1.5 sm:gap-3">
                                            <span className="text-muted-foreground min-w-0">
                                                {selectedDelivery === 'boxnow' ? 'Κόστος Box Now' : 'Αποστολή'}
                                            </span>
                                            {isFreeShipping ? (
                                                <span className="text-green-600 shrink-0">Δωρεάν</span>
                                            ) : (
                                                <span className="shrink-0 tabular-nums">€{shippingCost.toFixed(2)}</span>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* COD Fee */}
                                    {selectedPayment === 'cod' && codFee > 0 && (
                                        <div className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1.5 sm:gap-3">
                                            <span className="text-muted-foreground min-w-0">Χρέωση αντικαταβολίας</span>
                                            <span className="shrink-0 tabular-nums">€{codFee.toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    {/* Tax (if not included) */}
                                    {taxAmount > 0 && (
                                        <div className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1.5 sm:gap-3">
                                            <span className="text-muted-foreground min-w-0">ΦΠΑ ({shippingSettings.tax_rate}%)</span>
                                            <span className="shrink-0 tabular-nums">€{taxAmount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    {/* VAT info if included */}
                                    {shippingSettings.tax_enabled && shippingSettings.tax_included_in_price && (
                                        <p className="text-xs text-muted-foreground">
                                            * Οι τιμές περιλαμβάνουν ΦΠΑ {shippingSettings.tax_rate}%
                                        </p>
                                    )}
                                    
                                    <div className="flex justify-between font-semibold text-base sm:text-lg pt-2 border-t gap-3">
                                        <span className="min-w-0">Σύνολο</span>
                                        <span className="shrink-0 tabular-nums">€{totalWithFees.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                {/* Login Prompt Dialog */}
                <Dialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-display">
                                {authMode === 'login' ? 'Login to your account' : 'Create an account'}
                            </DialogTitle>
                            <DialogDescription>
                                {authMode === 'login' 
                                    ? 'Login to track your orders and earn rewards'
                                    : 'Create an account to track orders and get exclusive offers'
                                }
                            </DialogDescription>
                        </DialogHeader>

                        <p className="text-xs text-center text-muted-foreground -mt-1 mb-2">
                            <Link
                                to={buildLoginUrl('/checkout')}
                                className="underline hover:text-foreground"
                                onClick={() => setShowLoginPrompt(false)}
                            >
                                Πλήρης οθόνη σύνδεσης
                            </Link>
                            <span className="mx-1">·</span>
                            <Link
                                to={buildRegisterUrl('/checkout')}
                                className="underline hover:text-foreground"
                                onClick={() => setShowLoginPrompt(false)}
                            >
                                Εγγραφή (πλήρης οθόνη)
                            </Link>
                        </p>

                        <form onSubmit={handleAuth} className="space-y-4">
                            {authMode === 'register' && (
                                <div className="space-y-2">
                                    <Label htmlFor="auth-name">Full Name</Label>
                                    <Input
                                        id="auth-name"
                                        value={authForm.name}
                                        onChange={(e) => setAuthForm(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="John Doe"
                                        required={authMode === 'register'}
                                    />
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="auth-email">Email</Label>
                                <Input
                                    id="auth-email"
                                    type="email"
                                    value={authForm.email}
                                    onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="you@example.com"
                                    required
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="auth-password">Password</Label>
                                <Input
                                    id="auth-password"
                                    type="password"
                                    value={authForm.password}
                                    onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                                    placeholder="••••••••"
                                    required
                                />
                            </div>

                            {authMode === 'register' && (
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={authForm.subscribeNewsletter}
                                        onChange={(e) => setAuthForm(prev => ({ ...prev, subscribeNewsletter: e.target.checked }))}
                                        className="rounded border-gray-300"
                                    />
                                    <span className="text-sm text-muted-foreground">
                                        Subscribe to newsletter for exclusive offers
                                    </span>
                                </label>
                            )}

                            {authError && (
                                <p className="text-sm text-destructive">{authError}</p>
                            )}

                            <Button type="submit" className="w-full rounded-full" disabled={authLoading}>
                                {authLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : null}
                                {authMode === 'login' ? 'Login' : 'Create Account'}
                            </Button>
                        </form>

                        <div className="text-center text-sm">
                            {authMode === 'login' ? (
                                <p>
                                    Don't have an account?{' '}
                                    <button 
                                        onClick={() => setAuthMode('register')}
                                        className="text-primary hover:underline"
                                    >
                                        Register
                                    </button>
                                </p>
                            ) : (
                                <p>
                                    Already have an account?{' '}
                                    <button 
                                        onClick={() => setAuthMode('login')}
                                        className="text-primary hover:underline"
                                    >
                                        Login
                                    </button>
                                </p>
                            )}
                        </div>

                        <div className="relative my-4">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background px-2 text-muted-foreground">Or</span>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="space-y-2">
                                <Label htmlFor="guest-email">Συνέχεια ως επισκέπτης</Label>
                                <Input
                                    id="guest-email"
                                    type="email"
                                    value={guestEmail}
                                    onChange={(e) => setGuestEmail(e.target.value)}
                                    placeholder="Email (προαιρετικό – για ενημερώσεις παραγγελίας)"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Μπορείτε να αφήσετε το email κενό· δεν θα λάβετε email επιβεβαίωσης.
                                </p>
                            </div>
                            <Button 
                                variant="outline" 
                                className="w-full rounded-full"
                                onClick={handleContinueAsGuest}
                                data-testid="continue-as-guest-btn"
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                Συνέχεια χωρίς λογαριασμό
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
                <Footer />
            </div>
        </PayPalScriptProvider>
    );
}
